import cookie from '@tencent/vft-common/cookie';
import env from '@tencent/vft-common/env';
import logger from '@tencent/vft-common/logger';
import { getUrlParam } from '@tencent/vft-common/util.esm';

import {
  ALL_LOGIN_TYPES,
  COOKIE_EXPIRED_TIME,
  GUID_COOKIE,
  LOGIN_TYPE,
  PTLOGIN_COOKIE,
  TV_APPIDS,
  VIDEO_COOKIES,
} from './utils/config';
import {
  clearPageCookies,
  clearRefreshTimeCookies,
  clearRootDomainCookies,
  clearUserCookies,
  clearVideoDomainCookies,
  generateGuid,
  getDomain,
  getSkey,
  isNativeLogin,
  setUserInfo,
  time33,
} from './utils/helper';
import IframeCookieLoader from './utils/IframeCookieLoader';
import * as reporter from './utils/report';
import * as service from './utils/service';


/**
 * 判断当前是否是 video.qq.com 域
 * @returns {Boolean}
 */
function isVideoDomain() {
  return /\.video\.qq\.com$/.test(location.host);
}

/**
 * 获取登录态类型
 */
export function loginType() {
  const loginType = cookie.get('main_login');

  if (loginType === 'none') {
    return '';
  }

  return loginType || '';
}

/**
 * 同步登录态
 */
export async function syncCookies(callback) {
  // 通过平台号确定是否需要同步cookie
  // 如果当前业务获取不到 cookie 域名，则不做后续同步逻辑
  const cookieDomain = getDomain();

  if (env.isApp || isNativeLogin() || !cookieDomain) {
    callback && callback();
    return;
  }

  // video 子域名下需要种video_guid, 其他域名会通过cookie.html去同步
  if (isVideoDomain()) {
    if (!cookie.get('video_guid')) {
      cookie.set('video_guid', generateGuid(), {
        domain: '.video.qq.com',
        path: '/',
        expires: COOKIE_EXPIRED_TIME,
      });
    }
    callback && callback();
    return;
  }

  try {
    const cookies = await IframeCookieLoader.getInstance().getVideoCookie();
    ALL_LOGIN_TYPES.filter(type => type !== cookies.main_login).forEach((otherType) => {
      if (cookie.get(`${otherType}_nick`) || cookie.get(`${otherType}_head`)) {
        cookie.remove(`${otherType}_nick`, {
          domain: cookieDomain,
          path: '/',
        });
        cookie.remove(`${otherType}_head`, {
          domain: cookieDomain,
          path: '/',
        });
      }
    });

    // 同步 cookie + video_guid
    VIDEO_COOKIES.concat(GUID_COOKIE).forEach((key) => {
    // fix: 以前的逻辑 cookie 是种在当前域，需要先移出当前域名的 cookie 信息，再同步
    // 新逻辑 cookie 种在三级域名，如 m.v.qq.com，cookie 种在 v.qq.com
      if (cookieDomain !== location.host) {
        cookie.remove(key, {
          domain: location.host,
        });
      }
      cookie.set(key, cookies[key] || '', {
        expires: cookies[key] ? COOKIE_EXPIRED_TIME : -24,
        domain: cookieDomain,
        path: '/',
      });
    });
    callback && callback();
    reporter.report({
      f_event: reporter.EVENT_INIT_SYNC,
    });
  } catch (e) {
    logger.error('syncCookies caught error', e);
    callback && callback();
    reporter.report({
      f_event: reporter.EVENT_INIT_SYNC,
      f_errmsg: `syncCookies caught error = ${e.toString()}`,
    });
  }
}

let refreshTime = 0;
const maxRefreshTime = 2;

/**
 * 续期登录态
 */
export async function refreshAuthToken(type, callback) {
  if (typeof type === 'function') {
    callback = type;
    type = loginType() || LOGIN_TYPE.qq;
  }

  const appid = type === LOGIN_TYPE.qq ? cookie.get('vqq_appid') : cookie.get('appid');
  const cookieDomain = getDomain();

  // TV登录态不过续期或者当前域名不在白名单
  if (TV_APPIDS.indexOf(appid) > -1 || !cookieDomain) {
    return callback();
  }

  const csrfParam = buildCSRF();
  try {
    const data = await service.authRefresh({ type, ...csrfParam });
    const cookieOption = {
      expires: COOKIE_EXPIRED_TIME,
      domain: cookieDomain,
      path: '/',
    };
    // 无需再次同步登录态信息到 video.qq.com 域包括子域，避免冲突
    if (!isVideoDomain()) {
      if (type === LOGIN_TYPE.qq && cookie.get('vqq_appid') && cookie.get('vqq_openid')) {
        const PREFIX = 'vqq_';
        Object.keys(data).forEach((key) => {
          if (VIDEO_COOKIES.indexOf(key) > -1) {
            cookie.set(PREFIX + key, data[key] || '', {
              expires: data[key] ? COOKIE_EXPIRED_TIME : -24,
              domain: cookieDomain,
              path: '/',
            });
          }
        });
      } else {
        cookie.set('vuserid', data.vuserid, cookieOption);
        cookie.set('vusession', data.vusession, cookieOption);
      }
    }
    // 同步用户头像昵称，用于展示用
    cookie.set(`${type}_nick`, data.nick, cookieOption);
    cookie.set(`${type}_head`, data.head, cookieOption);
    cookie.set('last_refresh_time', +new Date(), cookieOption);
    cookie.set('last_refresh_vuserid', data.vuserid, cookieOption);
    updateAuthRefreshTimer(Number(data.next_refresh_time));
    callback && callback();
  } catch (e) {
    if ([-1001, -1002, -1003, -1004, 1006, 1111, 1112].indexOf(e.code) > -1) {
      clearCookie(() => callback?.(e));
    } else {
      if (refreshTime < maxRefreshTime) {
        refreshAuthToken(callback);
      } else {
        callback?.(e);
      }
      refreshTime += 1;
    }
  }
}

/**
 * 续期定时器
 * @param {Number} nextTime 下次续期时间/s
 */
let refreshTimer = null;
function updateAuthRefreshTimer(nextTime = 0) {
  if (refreshTimer) clearTimeout(refreshTimer);

  if (nextTime > 0) {
    refreshTimer = setTimeout(() => {
      refreshAuthToken();
      clearTimeout(refreshTimer);
    }, nextTime * 1000);
  }
}

let retryTime = 0;
export function innerToken2tenvideo(callback) {
  const msdkEncodeParam = getUrlParam('msdkEncodeParam');
  if (env.isMsdk && !msdkEncodeParam) {
    // 游戏场景，如果没有msdk参数，无法做票据转换，一般这种情况是已登录的case
    return callback();
  }
  // 先清Cookie，是为了避免拿到污染的票据信息
  // 由于换票据受限于客户端切换登录，不会同步清掉当前域下cookie
  // 在体育场景，登录类型字段 main_login 与主站一样，会造成切换账号后仍然拿到上一次票据信息
  clearCookie(() => {
    let loginType = '';
    const isMsdk = msdkEncodeParam.length > 0;
    let data = {};
    if (isMsdk) {
      data = {
        appid: getUrlParam('appid'),
        openid: getUrlParam('openid'),
        algorithm: getUrlParam('algorithm'),
        version: getUrlParam('version'),
        timestamp: +getUrlParam('timestamp'),
        sig: getUrlParam('sig'),
        encode: 2,
        msdkEncodeParam,
      };
      loginType = data.appid.match(/^[0-9]+$/) ? LOGIN_TYPE.qq : LOGIN_TYPE.wx;
    } else if (env.isWeishi) {
      // 腾讯微视支持使用票据转换打通登录态
      loginType = +cookie.get('iAuthType') === 1 ? LOGIN_TYPE.qq : LOGIN_TYPE.wx;
    } else if (env.isQQSports) {
      // 腾讯体育支持使用票据转换打通登录态
      loginType = cookie.get('main_login');
    } else if (env.isQQMusic) {
      // QQ音乐, 目前只支持QQ登录态换票据,微信由于缺少access_token
      const tmeLoginType = +cookie.get('tmeLoginType');

      if (tmeLoginType === 2) {
        loginType = LOGIN_TYPE.qq;
      } else if (tmeLoginType === 1) {
        loginType = LOGIN_TYPE.wx;
      }
    }

    if (!loginType) { // 未登录
      return callback();
    }
    if (env.isWeishi) {
      if (loginType === LOGIN_TYPE.qq && cookie.get('sSessionKey')) {
        cookie.set('accessToken', cookie.get('sSessionKey'), {
          domain: '.qq.com',
          path: '/',
        });
      }
      // 原因微视安卓端在登录态打通版本8.19.1前，也写了视频票据，但写的是空数据，导致cookie污染问题
      clearRootDomainCookies();
    }

    // type: 2 // 1|非游戏QQ互联，2|非游戏微信，3|游戏msdk
    let type;
    if (isMsdk) {
      type = 3;
    } else if (loginType === LOGIN_TYPE.qq) {
      type = 1;
    } else {
      type = 2;
    }

    service.thirdLogin(type, data)
      .then((res) => {
        setUserInfo(res);
        callback();
      })
      .catch((e) => {
        logger.error('innerToken2tenvideo caught error', e);
        if (retryTime < 1) {
          innerToken2tenvideo(callback);
          retryTime += 1;
        } else {
          callback();
        }
      });
  });
}

/**
 * 清除登录态
 * @param {String} [type] 账号类型
 * @param {Function} callback 回调
 */
export async function clearCookie(type, callback) {
  if ((cookie.get('uin') || cookie.get('luin'))) {
    PTLOGIN_COOKIE.forEach((key) => {
      cookie.remove(key, {
        domain: 'qq.com',
        path: '/',
      });
    });
  }

  if (typeof type === 'function') {
    callback = type;
    type = loginType() || LOGIN_TYPE.qq;
  }

  // 清除当前域下 Cookie 信息
  clearPageCookies();
  clearRefreshTimeCookies();
  clearUserCookies();

  await clearVideoDomainCookies();
  callback();
}

/**
 *
 * @param {String} type 账号类型
 * @param {Function} callback 回调函数
 */
export function authLogout(type) {
  const params = { type };

  const wxAppid = cookie.get('appid');
  if (type === LOGIN_TYPE.qq && wxAppid && wxAppid.match(/^wx/)) {
    // 20210521: 强匹配微信appid，因为存在老qq登录时，appid=3000501，这种appid会导致后台取值异常
    // 同时存在QQ和微信登录时，由于原先V站切互联时遇到一些未明原因的问题，juliehwang 在清QQ登录态时，把微信的 appid 也清了
    // 现在增加参数来确保不清微信的 appid
    params.keepwx = '1';
  }

  if (!params.type) {
    // 默认清掉微信和QQ在当前页面的票据，避免票据污染，特别在升级登录态打通场景
    // 容易出现 film.qq.com 的cookie与 qq.com 的cookie相互影响
    params.type = loginType() || LOGIN_TYPE.qq;
  }

  return service.authLogout(params);
}

/**
 * 根据登录态生成 gtk 加密串，用于校验视频票据
 * @return {Object}
 * @property {string} g_tk skey 票据.
 * @property {string} g_vstk 互联和微信 vusession 票据.
 * @property {string} g_actk 互联和微信 access_token 票据.
 */
export function buildCSRF() {
  const params = {};
  const type = loginType();

  if (!type) {
    return params;
  }

  if (type === LOGIN_TYPE.qq) {
    const skey = getSkey();
    if (skey) {
      params.g_tk = time33(skey);
    }
    const vs = cookie.get('vqq_vusession');
    if (vs) {
      params.g_vstk = time33(vs);
    }
    const ac = cookie.get('vqq_access_token');
    if (ac) {
      params.g_actk = time33(ac);
    }
  } else {
    const vs = cookie.get('vusession');
    if (vs) {
      params.g_vstk = time33(vs);
    }
    const ac = cookie.get('access_token');
    if (ac) {
      params.g_actk = time33(ac);
    }
  }
  return params;
}
